import { DATA_KEYS, readJSON, writeJSON } from "@/storage/local";

// Las métricas que trae la app de fábrica. El usuario puede añadir las suyas,
// que se guardan aparte para que un cambio en esta lista no las pise.
export const DEFAULT_METRICS = [
  { id: "weight", name: "Peso corporal", unit: "kg", icon: "scale" },
  { id: "bodyfat", name: "Grasa corporal", unit: "%", icon: "percent" },
  { id: "calories", name: "Ingesta calórica", unit: "kcal", icon: "flame" },
  { id: "neck", name: "Cuello", unit: "cm", icon: "body" },
  { id: "shoulders", name: "Hombros", unit: "cm", icon: "body" },
  { id: "chest", name: "Pecho", unit: "cm", icon: "body" },
  { id: "bicepL", name: "Bíceps izquierdo", unit: "cm", icon: "body" },
  { id: "bicepR", name: "Bíceps derecho", unit: "cm", icon: "body" },
  { id: "forearmL", name: "Antebrazo izquierdo", unit: "cm", icon: "body" },
  { id: "forearmR", name: "Antebrazo derecho", unit: "cm", icon: "body" },
  { id: "waist", name: "Cintura", unit: "cm", icon: "body" },
  { id: "hips", name: "Caderas", unit: "cm", icon: "body" },
  { id: "thighL", name: "Muslo izquierdo", unit: "cm", icon: "body" },
  { id: "thighR", name: "Muslo derecho", unit: "cm", icon: "body" },
  { id: "calfL", name: "Pantorrilla izquierda", unit: "cm", icon: "body" },
  { id: "calfR", name: "Pantorrilla derecha", unit: "cm", icon: "body" },
];

// ─── Métricas personalizadas ──────────────────────────────────────────────────

export function loadCustomMetrics() {
  return readJSON(DATA_KEYS.CUSTOM_METRICS, []);
}

export function addCustomMetric(name, unit) {
  const metrics = loadCustomMetrics();
  const metric = { id: `custom_${Date.now()}`, name, unit: unit || "cm", icon: "body", custom: true };
  writeJSON(DATA_KEYS.CUSTOM_METRICS, [...metrics, metric]);
  return metric;
}

export function deleteCustomMetric(id) {
  writeJSON(
    DATA_KEYS.CUSTOM_METRICS,
    loadCustomMetrics().filter((m) => m.id !== id)
  );
  // Las lecturas ya registradas de esa métrica se quedan huérfanas a propósito:
  // si el usuario la vuelve a crear con el mismo nombre no recupera nada, pero
  // borrar un historial por deshacer un añadido sería peor.
}

export function getAllMetrics() {
  return [...DEFAULT_METRICS, ...loadCustomMetrics()];
}

// ─── Lecturas ─────────────────────────────────────────────────────────────────
//
// Estructura: { [metricId]: [{ date, value }, ...] }, ordenadas por fecha.

// El peso corporal vivía en su propia clave, con forma {date, weight}. Ahora es
// una métrica más, así que se trasvasa la primera vez que se leen las medidas —
// si no, el historial de peso del usuario desaparecería de la interfaz.
function migrateLegacyBodyWeight(all) {
  if (all.weight?.length > 0) return all;

  const legacy = readJSON(DATA_KEYS.BODYWEIGHT, []);
  if (!Array.isArray(legacy) || legacy.length === 0) return all;

  const migrated = {
    ...all,
    weight: legacy.map((e) => ({ date: e.date, value: e.weight })),
  };
  writeJSON(DATA_KEYS.MEASUREMENTS, migrated);
  return migrated;
}

export function loadMeasurements() {
  return migrateLegacyBodyWeight(readJSON(DATA_KEYS.MEASUREMENTS, {}));
}

export function recordMeasurement(metricId, value) {
  if (value === null || value === undefined || isNaN(value)) return null;

  const all = loadMeasurements();
  const entries = all[metricId] || [];
  const today = new Date().toISOString().split("T")[0];

  // Una lectura por métrica y día: apuntar el bíceps tres veces la misma tarde
  // no son tres puntos en la gráfica, es una corrección.
  const todayIdx = entries.findIndex((e) => e.date.startsWith(today));
  if (todayIdx >= 0) entries[todayIdx] = { ...entries[todayIdx], value };
  else entries.push({ date: new Date().toISOString(), value });

  all[metricId] = entries.slice(-365);
  writeJSON(DATA_KEYS.MEASUREMENTS, all);
  return all[metricId];
}

export function deleteMeasurement(metricId, index) {
  const all = loadMeasurements();
  const entries = all[metricId] || [];
  entries.splice(index, 1);
  all[metricId] = entries;
  writeJSON(DATA_KEYS.MEASUREMENTS, all);
  return entries;
}

// Último valor y variación respecto a la lectura anterior.
export function getMetricSummary(metricId) {
  const entries = loadMeasurements()[metricId] || [];
  if (entries.length === 0) return { last: null, delta: null, count: 0 };

  const last = entries[entries.length - 1];
  const prev = entries.length > 1 ? entries[entries.length - 2] : null;
  return {
    last: last.value,
    lastDate: last.date,
    delta: prev ? Number((last.value - prev.value).toFixed(2)) : null,
    count: entries.length,
  };
}
